import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Skeleton, Row, Col, Typography, Button } from 'antd';

import {
  StudentWithProps,
  useGetStudents,
} from '../../../hooks/useGetStudents';
import { PrimaryButton } from '../../../components/PrimaryButton';
import StudentDiv from './StudentDiv';

import EditStudentModal from '../modals/EditStudentModal';
import { Student } from '../../../models/student.model';
import { useGetPromotionDay } from '../../../hooks/useGetPromotionDay';
import dayjs from 'dayjs';
import { missingProfileItems, qualified } from '../../../utils/studentHelpers';

const { Title, Paragraph } = Typography;

const NoStudentsState = () => {
  return (
    <div style={{ textAlign: 'center', marginTop: 24 }}>
      <Paragraph>
        Your family account is missing students.
        <br />
        To enroll in programs, please add your students in your family account.
        <br />
        If this message persists, please email techsupport@starsacramento.org
        for assistance.
      </Paragraph>
      <a
        href={`https://my.starsacramento.org/`}
        rel="noopener noreferrer"
        target="_blank"
      >
        <Button type="primary">Your Family Account</Button>
      </a>

      <Paragraph style={{ marginTop: 16 }}>
        Maybe you just want to see what we have to offer?
        <br />
        If So,
      </Paragraph>
      <Link to="/scheduling">
        <Button>Look at Programs</Button>
      </Link>
    </div>
  );
};

const StudentsErrorState = () => {
  return (
    <div style={{ textAlign: 'center', marginTop: 24 }}>
      <Paragraph>
        We could not load your students right now.
        <br />
        Please refresh and try again.
        <br />
        If this message persists, please email techsupport@starsacramento.org
        for assistance.
      </Paragraph>
    </div>
  );
};

const StudentsDiv = () => {
  const navigate = useNavigate();
  const students = useGetStudents();

  const [editStudent, setEditStudent] = React.useState<Student | null>(null);
  const [editModalOpen, setEditModalOpen] = React.useState<boolean>(false);

  const getPromotionDay = useGetPromotionDay();
  const promotionDay = dayjs(getPromotionDay.data);
  const beforePromotionDay = getPromotionDay.data
    ? dayjs().isBefore(promotionDay)
    : false;

  const notQualified = (student: StudentWithProps) => {
    return !qualified(student);
  };

  const handleOnEdit = (student: Student) => {
    setEditStudent(student);
    setEditModalOpen(true);
  };

  const handleEditStudentModalClose = () => {
    setEditModalOpen(false);
    students.refetch();
  };

  if (students.isLoading) {
    return <Skeleton active />;
  }

  if (students.isError) {
    return <StudentsErrorState />;
  }

  if (!students.data?.length) {
    return <NoStudentsState />;
  }

  const ProceedToScheduling = () => {
    return (
      <>
        {students.data.filter(qualified).length > 0 && (
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <Paragraph>
              Does Your Student Information Look Correct?
              <br />
              If So,
            </Paragraph>
            <PrimaryButton
              onClick={() => navigate('/scheduling')}
              type="primary"
              size={'large'}
              block
            >
              Proceed To Scheduling
            </PrimaryButton>
          </div>
        )}
      </>
    );
  };

  if (students.data?.length) {
    return (
      <>
        <Row style={{ marginBottom: 16 }}>
          <Col>
            <Title level={4}>Your Students</Title>
          </Col>
        </Row>

        <div id="studentList">
          {/** Render "qualified" students */}
          {students.data.filter(qualified).map((student, index) => (
            <StudentDiv
              key={index}
              student={student}
              qualified={true}
              beforePromotionDay={beforePromotionDay}
              onEdit={handleOnEdit}
            />
          ))}

          {/** Render "not qualified" students */}
          {students.data.filter(notQualified).map((student, index) => (
            <StudentDiv
              key={index}
              student={student}
              qualified={false}
              missingItems={missingProfileItems(student)}
              beforePromotionDay={beforePromotionDay}
              onEdit={handleOnEdit}
            />
          ))}
        </div>

        <ProceedToScheduling />

        {editStudent && (
          <EditStudentModal
            isOpen={editModalOpen}
            onClose={handleEditStudentModalClose}
            student={editStudent}
          />
        )}
      </>
    );
  }

  return <NoStudentsState />;
};

export default StudentsDiv;
